import React, { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider, LANGS } from "@/i18n";
import { AuthProvider } from "@/context/AuthContext";
import PublicLayout from "@/components/PublicLayout";
import AdminLayout from "@/components/AdminLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Opportunities from "@/pages/public/Opportunities";
import RealEstate from "@/pages/public/RealEstate";
import HumanDevelopment from "@/pages/public/HumanDevelopment";
import Projects from "@/pages/public/Projects";
import Careers from "@/pages/public/Careers";
import Contact from "@/pages/public/Contact";
// Apply.jsx (legacy 4-step form) is kept as a historical file and is intentionally not routed:
// the canonical application form is Join (V1.1 consents + CRM connector) served on the apply slug.
import { PrivacyPage, TermsPage } from "@/pages/public/Legal";
import { INDEXABLE_KEYS, PUBLIC_SLUGS, legacyRedirects, routeSlug } from "@/i18n/routes";
import RecruiterStats from "@/pages/admin/RecruiterStats";
import { Login, Register, ForgotPassword, ResetPassword, VerifyEmail } from "@/pages/auth/AuthPages";
import Portal from "@/pages/portal/Portal";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCandidates from "@/pages/admin/AdminCandidates";
import AdminCandidateDetail from "@/pages/admin/AdminCandidateDetail";
import AdminPipeline from "@/pages/admin/AdminPipeline";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminLiveChat from "@/pages/admin/AdminLiveChat";
import AdminContact from "@/pages/admin/AdminContact";
import AdminRecruitOps from "@/pages/admin/AdminRecruitOps";
import { captureLanding } from "@/lib/attribution";
import AdminRecruitment from "@/pages/admin/AdminRecruitment";
import Join from "@/pages/public/Join";
import ScrollToTop from "@/components/ScrollToTop";

const PUBLIC_ROUTES = [
  ["login", <Login />],
  ["register", <Register />],
  ["forgot-password", <ForgotPassword />],
  ["reset-password", <ResetPassword />],
  ["verify-email", <VerifyEmail />],
];

const PAGE_ELEMENTS = {
  home: <Home />,
  about: <About />,
  opportunities: <Opportunities />,
  realEstate: <RealEstate />,
  careers: <Careers />,
  humanDev: <HumanDevelopment />,
  projects: <Projects />,
  contact: <Contact />,
  privacy: <PrivacyPage />,
  terms: <TermsPage />,
};

// Localized public slugs (/fr/immobilier, /en/real-estate, /es/inmobiliario ...).
// Legacy English slugs stay reachable and redirect to the localized canonical URL.
function localizedRoutes(lang) {
  return INDEXABLE_KEYS.filter((key) => key !== "apply")
    .map((key) => [routeSlug(key, lang).slice(1), PAGE_ELEMENTS[key]]);
}

// Every localized "join" slug now points to the single canonical application URL.
const JOIN_SLUGS = Object.values(PUBLIC_SLUGS.join);
function LangShell() {
  const { lang } = useParams();
  if (!LANGS.includes(lang)) {
    return <Navigate to="/fr" replace />;
  }
  return (
    <I18nProvider lang={lang}>
      <AuthProvider>
        <ScrollToTop />
        <Routes>
          <Route
            path="portal"
            element={
              <ProtectedRoute allow="candidate">
                <Portal />
              </ProtectedRoute>
            }
          />
          <Route
            path="admin/*"
            element={
              <ProtectedRoute allow="staff">
                <AdminLayout>
                  <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="candidates" element={<AdminCandidates />} />
                    <Route path="candidates/:candidateId" element={<AdminCandidateDetail />} />
                    <Route path="pipeline" element={<AdminPipeline />} />
                    <Route path="stats" element={<RecruiterStats />} />
                    <Route path="recruitment" element={<AdminRecruitment />} />
                    <Route path="recruit-ops" element={<AdminRecruitOps />} />
                    <Route path="users" element={<ProtectedRoute allow="admin"><AdminUsers /></ProtectedRoute>} />
                    <Route path="audit" element={<ProtectedRoute allow="admin"><AdminAudit /></ProtectedRoute>} />
                    <Route path="settings" element={<ProtectedRoute allow="admin"><AdminSettings /></ProtectedRoute>} />
                    <Route path="livechat" element={<ProtectedRoute allow="admin"><AdminLiveChat /></ProtectedRoute>} />
                    <Route path="contact" element={<ProtectedRoute allow="admin"><AdminContact /></ProtectedRoute>} />
                    <Route path="*" element={<Navigate to="." replace />} />
                  </Routes>
                </AdminLayout>
              </ProtectedRoute>
            }
          />
          {/* Canonical application form: /{lang}/candidature. Standalone shell (no marketing header/footer). */}
          <Route path={routeSlug("apply", lang).slice(1)} element={<Join />} />
          {JOIN_SLUGS.filter((slug) => slug !== routeSlug("apply", lang)).map((slug) => (
            <Route
              key={`join-${slug}`}
              path={slug.slice(1)}
              element={<Navigate to={`/${lang}${routeSlug("apply", lang)}`} replace />}
            />
          ))}
          {[...localizedRoutes(lang), ...PUBLIC_ROUTES].map(([path, element]) => (
            <Route key={path || "index"} path={path} element={<PublicLayout>{element}</PublicLayout>} />
          ))}
          {legacyRedirects(lang).map(({ key, from, to }) => (
            <Route key={`legacy-${key}`} path={from.slice(1)} element={<Navigate to={`/${lang}${to}`} replace />} />
          ))}
          <Route path="*" element={<Navigate to={`/${lang}`} replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}

export default function App() {
  // Explicit call: a module-level side effect is dropped by the production minifier.
  useEffect(() => {
    captureLanding();
  }, []);
  return (
    <BrowserRouter>
      <Routes>
        {/* Root always resolves to the French canonical identity, in one redirect. */}
        <Route path="/" element={<Navigate to="/fr" replace />} />
        <Route path="/:lang/*" element={<LangShell />} />
      </Routes>
    </BrowserRouter>
  );
}
